import { CContainer } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilCheckCircle, cilLockLocked, cilShieldAlt } from '@coreui/icons'
import { useQuery } from '@tanstack/react-query'
import { publicApi, apiErrorMessage } from '../../api/client'
import { ErrorState, LoadingState } from '../../components/common/AsyncState'
import { SectionHeader } from '../../components/common/SectionHeader'
import { formatDateTime } from '../../utils/format'

export function StewardshipPage() {
  const query = useQuery({ queryKey: ['public', 'stewardship'], queryFn: publicApi.stewardship })
  return (
    <>
      <section className="page-hero page-hero--stewardship">
        <CContainer xxl>
          <span className="eyebrow">Preservation & governance</span>
          <h1>How LARICE protects the machine and the meaning of its public record.</h1>
          <p>
            Definitions, review controls, maintenance milestones and ledger verification are stated
            openly so the figures can be interpreted correctly.
          </p>
        </CContainer>
      </section>
      <section className="section-space--tight">
        <CContainer xxl>
          {query.isLoading && <LoadingState />}
          {query.isError && <ErrorState message={apiErrorMessage(query.error)} />}
          {query.data && (
            <>
              <SectionHeader
                eyebrow="Measurement definitions"
                title="What each public indicator means"
                description="The portal separates observed processing, service presence, reservation, maintenance and ready capacity."
              />
              <div className="definition-grid">
                {query.data.definitions.map((item) => (
                  <article key={item.term}>
                    <strong>{item.term}</strong>
                    <p>{item.definition}</p>
                    {item.formula && <code>{item.formula}</code>}
                  </article>
                ))}
              </div>
              <div className="stewardship-grid mt-5">
                <article className="panel">
                  <span className="eyebrow">Maintenance record</span>
                  <h2>Last completed</h2>
                  {query.data.lastMaintenance ? (
                    <>
                      <h3>{query.data.lastMaintenance.title}</h3>
                      <p>
                        {query.data.lastMaintenance.publicSummary ??
                          'Completed stewardship activity recorded by LARICE operations.'}
                      </p>
                      <strong>
                        {formatDateTime(
                          query.data.lastMaintenance.endsAt ?? query.data.lastMaintenance.startsAt,
                        )}
                      </strong>
                    </>
                  ) : (
                    <p>No public maintenance record is available.</p>
                  )}
                </article>
                <article className="panel">
                  <span className="eyebrow">Next planned action</span>
                  <h2>Preservation calendar</h2>
                  {query.data.nextMaintenance ? (
                    <>
                      <h3>{query.data.nextMaintenance.title}</h3>
                      <p>
                        {query.data.nextMaintenance.publicSummary ??
                          'Planned stewardship activity for the shared H100 system.'}
                      </p>
                      <strong>{formatDateTime(query.data.nextMaintenance.startsAt)}</strong>
                    </>
                  ) : (
                    <p>No future public maintenance is scheduled.</p>
                  )}
                </article>
                <article className="panel ledger-head">
                  <CIcon icon={cilShieldAlt} />
                  <span className="eyebrow">Ledger head</span>
                  <h2>Sequence #{query.data.ledgerHead.sequence}</h2>
                  <code>{query.data.ledgerHead.hash}</code>
                  <p>Last verified {formatDateTime(query.data.ledgerHead.verifiedAt)}</p>
                </article>
              </div>
              <SectionHeader
                eyebrow="Governance controls"
                title="Transparency without exposing operations"
                description="The public layer is generated from reviewed records rather than raw logs."
              />
              <div className="governance-grid">
                {query.data.governance.map((item, index) => (
                  <article key={item.title}>
                    <CIcon icon={index % 2 ? cilLockLocked : cilCheckCircle} />
                    <div>
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </CContainer>
      </section>
    </>
  )
}
